import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import {
  DeviceIds,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  OperatingStateProperties,
  RoomCommands,
  RoomProperties,
  RoomsOperatingStateModifier,
} from '@vi-iot/client';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GTMService, UIService } from '@services';
import {
  DevicesStore,
  FeatureService,
  GatewayStore,
  InstallationFeaturesStore,
  RemoteDeviceStore,
  UnassignableDeviceDisconnected,
} from '@store';

import { environment } from 'src/environments/environment';
import { SingleRoomControlOverlayTypeEnum } from '../../../../+installations/installation/installation-overlay.model';
import { PillType } from '../../../../shared/components/pill/pill.model';
import { InstallationService } from '../../../services/installation.service';
import { SingleRoomControlComponent } from '../../single-room-control.component';
import { CommissioningService } from '../commissioning/commissioning.service';
import { ZigbeeTopologyModalComponent } from '../zigbee-topology-modal/zigbee-topology-modal.component';
import { AllAddedDevicesModalComponent } from '../src-commissioning-overlay/commissioning-overview/all-added-devices-modal/all-added-devices-modal.component';

@Component({
  selector: 'app-src-details-overlay',
  templateUrl: './src-details-overlay.component.html',
  styleUrls: ['./src-details-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SrcDetailsOverlayComponent extends SingleRoomControlComponent implements OnInit {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly buyRepeaterLink = environment.buyRepeaterLink;
  readonly viessmannShop = environment.viessmannShop;
  readonly pillType = PillType;
  @Input() installationId: number;
  @Input() gatewaySerial: string;
  RoomsOperatingStateModifier = RoomsOperatingStateModifier;
  roomStatusFeatureUpdatePending$: Observable<boolean>;
  unAssignableDeviceStatus$: Observable<UnassignableDeviceDisconnected>;
  operatingState$: Observable<Feature<OperatingStateProperties, never>>;
  allAddedDevicesDialogRef: MatDialogRef<AllAddedDevicesModalComponent>;

  constructor(
    public remoteDeviceStore: RemoteDeviceStore,
    public gatewayStore: GatewayStore,
    public devicesStore: DevicesStore,
    protected featureService: FeatureService,
    protected translate: TranslateService,
    protected installationService: InstallationService,
    protected gtmService: GTMService,
    protected commissioningService: CommissioningService,
    private uiService: UIService,
    private dialog: MatDialog,
    protected installationFeaturesStore: InstallationFeaturesStore
  ) {
    super(
      gatewayStore,
      devicesStore,
      featureService,
      translate,
      installationService,
      gtmService,
      commissioningService,
      installationFeaturesStore
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.unAssignableDeviceStatus$ = this.installationService.isAnyDeviceDisconnected();
  }

  openZigbeeTopologyDialog(): void {
    this.dialog.open(ZigbeeTopologyModalComponent, {
      width: '1280px',
      height: 'auto',
      panelClass: 'zigbee-topology-dialog',
    });
  }

  openCommissioningOverlay(): void {
    this.uiService.openSingleRoomControlOverlay(
      SingleRoomControlOverlayTypeEnum.SingleRoomControlCommissioning,
      this.installationId,
      this.gatewaySerial
    );
  }

  openSrcSettingsOverlay(): void {
    this.uiService.openSingleRoomControlOverlay(
      SingleRoomControlOverlayTypeEnum.SingleRoomControlSettings,
      this.installationId,
      this.gatewaySerial
    );
  }

  openAllAddedDevicesDialog(): void {
    this.allAddedDevicesDialogRef = this.dialog.open(AllAddedDevicesModalComponent, {
      width: '1084px',
      panelClass: 'commissioning-all-added-devices-dialog',
      data: {},
      autoFocus: false,
      disableClose: true,
    });
  }

  getRoom(roomId: string): Observable<Feature<RoomProperties, RoomCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomDetails(roomId),
      DeviceIds.RoomControl
    );
  }

  getRoomOperatingStateModifier(
    roomId: string
  ): Observable<Feature<OperatingStateProperties, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature<
      OperatingStateProperties,
      never
    >(FeatureNames.roomOperatingState(roomId), DeviceIds.RoomControl);
  }
}
